import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../store";
import {
  ConsultingCustomer,
  CustomerRole,
  ConsultingPath,
  ConsultingApply,
  ConsultingType,
} from "@src/types/consulting";
// type
import { PartnerType, PartnerTypeName } from "@src/define/partner";

// 인트로 보여줄 파트너 타입
export const showIntroPartnerType = [
  PartnerType.Studio,
  PartnerType.Dress,
  PartnerType.Makeup,
  PartnerType.Snap,
];

export type SelectedConsultingApply = Omit<
  ConsultingApply,
  "customer" | "visitDt" | "region" | "minBudget" | "maxBudget" | "interest"
> & {
  region: { code: string; name: string };
  budget: number[];
  interest: string[];
};

// 예산 기본 데이터
export const defaultBudget = [3000000, 5000000];

// 관심 품목
export const defaultInterest = ["스튜디오", "드레스", "메이크업", "본식스냅"];

export const initialCustomerState: ConsultingCustomer = {
  role: CustomerRole.BRIDE,
  name: "",
  tel: "",
  mateName: "",
  mateTel: "",
};

export const initialApplyState: SelectedConsultingApply = {
  customerUuid: "",
  inpath: ConsultingPath.WITH_PLANER,
  funnel: "",
  consultingApplyType: ConsultingType.NON_CONTACT, // 우선 전부 비대면으로 설정
  region: { code: "", name: "" },
  budget: defaultBudget,
  interest: [],
  partnerUuid: "",
  referer: "",
};

export const initialExtraState = {
  isBudgetPending: false, // 예산 미정
  agreement: false, // 개인정보 제공 동의
  partnerType: null, // 파트너 타입
  needsAssignment: false, // 배정 필요 여부
};

export interface ConsultingState {
  state: "in-progress" | "submitting" | "completed" | null;
  customerInfo: ConsultingCustomer;
  applyInfo: SelectedConsultingApply;
  extraInfo: {
    isBudgetPending: boolean;
    agreement: boolean;
    partnerType?: number | null;
    needsAssignment?: boolean;
  };
}

const initialState: ConsultingState = {
  state: null,
  customerInfo: initialCustomerState,
  applyInfo: initialApplyState,
  extraInfo: initialExtraState,
};

const consultingSlice = createSlice({
  name: "consulting",
  initialState,
  reducers: {
    setResetData(state) {
      state.state = null;
      state.customerInfo = initialCustomerState;
      state.applyInfo = initialApplyState;
      state.extraInfo = initialExtraState;
    },
    setAllData(state, action: PayloadAction<{ data: any }>) {
      let newData = action.payload.data;
      state.state = newData.state;
      state.customerInfo = {
        ...newData.customerInfo,
      };
      state.applyInfo = {
        ...newData.applyInfo,
      };
      state.extraInfo = {
        ...newData.extraInfo,
      };
    },
    setState(
      state,
      action: PayloadAction<{ state: "in-progress" | "submitting" | "completed" | null }>,
    ) {
      state.state = action.payload.state;
    },
    setCustomerInfo(state, action: PayloadAction<Partial<ConsultingCustomer>>) {
      state.customerInfo = { ...state.customerInfo, ...action.payload };
    },
    setApplyInfo(state, action: PayloadAction<Partial<SelectedConsultingApply>>) {
      state.applyInfo = { ...state.applyInfo, ...action.payload };
    },
    setExtraInfo(
      state,
      action: PayloadAction<Partial<{ isBudgetPending: boolean; agreement: boolean }>>,
    ) {
      state.extraInfo = { ...state.extraInfo, ...action.payload };
    },
  },
});

export const { setResetData, setAllData, setCustomerInfo, setApplyInfo, setExtraInfo, setState } =
  consultingSlice.actions;
export const ConsultingData = (state: RootState) => state.consulting;
export default consultingSlice.reducer;
